import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    ListItem,
    ListItemText,
    Button,
    IconButton,
    Chip,
    DialogActions,
    Tooltip,
    Box,
    CircularProgress
} from '@mui/material';
import {
    Close as CloseIcon,
    Assignment as AssignmentIcon,
    Check as CheckIcon,
    Delete as DeleteIcon,
    Warning as WarningIcon
} from '@mui/icons-material';

const PostAssignmentDialog = ({
    open,
    onClose,
    posts = [],
    extractedData,
    cv,
    applicationId,
    setRefreshcandidates,
    initialAssignments = [],
    loading = false,
    onLoadMorePosts = null,
    hasMorePosts = false,
    isLoadingMore = false
}) => {
    const { t } = useTranslation();
    const [assignedPosts, setAssignedPosts] = useState(new Set(initialAssignments));
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [extractedInfo, setExtractedInfo] = useState(null);
    const [saveResult, setSaveResult] = useState({ successCount: 0, failureCount: 0 });
    const apiUrl = process.env.REACT_APP_API_URL;
    // Only reset state when dialog opens
    useEffect(() => {
        console.log('🔍 EXTRACTED DATA24:', extractedData);
        // Set it to state
        setExtractedInfo(extractedData);
    }, [extractedData]);
    useEffect(() => {
        if (open) {
            setAssignedPosts(new Set(initialAssignments));
            setShowConfirmation(false);
            setIsSaving(false);
            setSaveResult({ successCount: 0, failureCount: 0 });
        }
    }, [open]);

    const handleAssignToggle = useCallback((postId) => {
        setAssignedPosts(prev => {
            const newSet = new Set(prev);
            if (newSet.has(postId)) {
                newSet.delete(postId);
            } else {
                newSet.add(postId);
            }
            return newSet;
        });
    }, []);

    // Function to delete submission after successful application creation
    const handleDelete = useCallback(async (applicationId) => {
        try {
            await axios.delete(`${apiUrl}authentication/delete-submission/${applicationId}/`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                },
            });
            return true;
        } catch (err) {
            console.error('Error deleting submission:', err);
            return false;
        }
    }, [apiUrl]);

    const sendApplication = useCallback(async (postId) => {
        try {
            const token = localStorage.getItem('accessToken');
            console.log('here is the extracted send data: ',extractedInfo)
            const response = await axios.post(
                `${apiUrl}candidates/applicationspontanee/switch/`,
                {
                    post_id: postId,
                    resume_path: cv,
                    extracted_informations: extractedInfo,
                    submission_id: applicationId, // Pass the submission ID for direct reference
                },

                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return { success: true, data: response.data };
        } catch (error) {
            console.error('Error sending application:', error.response?.data || error.message);
            return { success: false, error };
        }
    }, [apiUrl, cv, extractedInfo, applicationId]);

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        let successCount = 0;
        let failureCount = 0;

        // Process all assignments first
        const assignments = Array.from(assignedPosts);
        for (const postId of assignments) {
            try {
                // Create the application
                const applicationResult = await sendApplication(postId);

                if (applicationResult.success) {
                    successCount++;
                } else {
                    failureCount++;
                }
            } catch (error) {
                failureCount++;
                console.error(`Error processing assignment for post ${postId}:`, error);
            }
        }

        // Note: We no longer delete the submission to allow multiple assignments
        // The submission remains in DynamicFormSubmission table for future assignments

        setSaveResult({
            successCount,
            failureCount,
            deletionSuccess: true // Always true since we don't delete anymore
        });
        setShowConfirmation(true);
        setIsSaving(false);
       
    }, [assignedPosts, sendApplication]);

    const handleClose = useCallback(() => {
        onClose();
        // setRefreshcandidates(true);
    }, [onClose]);

    const hasChanges = useCallback(() => {
        const initialSet = new Set(initialAssignments);
        return (
            initialSet.size !== assignedPosts.size ||
            Array.from(initialSet).some(id => !assignedPosts.has(id))
        );
    }, [initialAssignments, assignedPosts]);

    const handleClearAll = useCallback(() => {
        setAssignedPosts(new Set());
    }, []);

    // Format deadline with proper localization
    const formatDeadline = useCallback((deadline) => {
        if (!deadline) return 'No deadline';
        try {
            const date = new Date(deadline);
            if (isNaN(date)) return 'Invalid date';
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return 'Invalid date';
        }
    }, []);

    // Check if deadline has passed
    const isDeadlineExpired = useCallback((deadline) => {
        if (!deadline) return false;
        try {
            const deadlineDate = new Date(deadline);
            const today = new Date();
            return deadlineDate < today;
        } catch {
            return false;
        }
    }, []);

    // Infinite scroll handler
    const handleScroll = useCallback((event) => {
        const { scrollTop, scrollHeight, clientHeight } = event.target;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // 100px threshold
        
        if (isNearBottom && hasMorePosts && !isLoadingMore && onLoadMorePosts) {
            onLoadMorePosts();
        }
    }, [hasMorePosts, isLoadingMore, onLoadMorePosts]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
        >
            <DialogTitle sx={{
                m: 0,
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'primary.main',
                color: 'common.white'
            }}>
                <Box display="flex" alignItems="center">
                    <AssignmentIcon sx={{ mr: 1.5 }} />
                    {t('postAssignmentDialog.title')}
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{ color: 'common.white' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ py: 2 }}>
                {showConfirmation ? (
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        py={4}
                    >
                        {saveResult.failureCount === 0 ? (
                            <>
                                <CheckIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                                <Box fontSize="h6.fontSize" textAlign="center">
                                    {t('postAssignmentDialog.successMessage', { count: saveResult.successCount })}
                                </Box>
                            </>
                        ) : (
                            <>
                                <WarningIcon sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} />
                                <Box fontSize="h6.fontSize" textAlign="center">
                                    {saveResult.successCount > 0 ? (
                                        <span>
                                            {t('postAssignmentDialog.partialSuccessMessage', { 
                                                successCount: saveResult.successCount, 
                                                failureCount: saveResult.failureCount 
                                            })}
                                        </span>
                                    ) : (
                                        <span>{t('postAssignmentDialog.failureMessage')}</span>
                                    )}
                                </Box>
                            </>
                        )}
                        <Button
                            variant="contained"
                            sx={{ mt: 3, px: 4 }}
                            onClick={handleClose}
                        >
                            {t('postAssignmentDialog.close')}
                        </Button>
                    </Box>
                ) : (
                    <>
                        <Box mb={2} display="flex" justifyContent="space-between">
                            <Box>
                                <Chip
                                    label={`${assignedPosts.size} ${t('postAssignmentDialog.selected')}`}
                                    color="primary"
                                    size="small"
                                    sx={{ mr: 1 }}
                                />
                                <Chip
                                    label={`${posts.length} ${t('postAssignmentDialog.available')}`}
                                    variant="outlined"
                                    size="small"
                                />
                            </Box>
                            {assignedPosts.size > 0 && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<DeleteIcon />}
                                    onClick={handleClearAll}
                                    sx={{ color: 'error.main' }}
                                >
                                    {t('postAssignmentDialog.clearAll')}
                                </Button>
                            )}
                        </Box>

                        <List 
                            dense 
                            sx={{ maxHeight: 400, overflow: 'auto' }}
                            onScroll={handleScroll}
                        >
                            {loading ? (
                                <ListItem sx={{ justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                </ListItem>
                            ) : posts.length > 0 ? (
                                <>
                                    {posts.map((post) => {
                                        const isAssigned = assignedPosts.has(post.id);
                                        const isAlreadyAssigned = post.is_assigned; // From backend
                                        const deadlineExpired = isDeadlineExpired(post.deadline);

                                        return (
                                            <ListItem
                                                key={post.id}
                                                sx={{
                                                    py: 1,
                                                    borderBottom: '1px solid',
                                                    borderColor: 'divider',
                                                    '&:hover': {
                                                        backgroundColor: 'action.hover',
                                                    }
                                                }}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            {post.title || `Position #${post.id}`}
                                                            {isAlreadyAssigned && (
                                                                <Chip
                                                                    label={t('postAssignmentDialog.alreadyAssigned')}
                                                                    size="small"
                                                                    color="info"
                                                                    variant="outlined"
                                                                    sx={{ fontSize: '0.75rem' }}
                                                                />
                                                            )}
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <>
                                                            {post.department && (
                                                                <Box component="span" display="block">
                                                                    {t('postAssignmentDialog.department')} {post.department}
                                                                </Box>
                                                            )}
                                                            <Box
                                                                component="span"
                                                                display="flex"
                                                                alignItems="center"
                                                                color={deadlineExpired ? 'error.main' : 'text.secondary'}
                                                                sx={{ mt: 0.5 }}
                                                            >
                                                                {deadlineExpired && (
                                                                    <WarningIcon fontSize="small" sx={{ mr: 0.5 }} />
                                                                )}
                                                                {t('postAssignmentDialog.deadline')} {formatDeadline(post.deadline)}
                                                            </Box>
                                                        </>
                                                    }
                                                    sx={{ pr: 2 }}
                                                />
                                                <Button
                                                    variant={isAssigned ? 'contained' : 'outlined'}
                                                    size="small"
                                                    startIcon={isAssigned ? <CheckIcon /> : <AssignmentIcon />}
                                                    onClick={() => handleAssignToggle(post.id)}
                                                    disabled={deadlineExpired || isSaving || isAlreadyAssigned}
                                                    sx={{
                                                        minWidth: 110,
                                                        backgroundColor: isAssigned ? 'success.light' : undefined,
                                                        '&:hover': {
                                                            backgroundColor: isAssigned ? 'success.main' : 'primary.light',
                                                            color: isAssigned ? 'common.white' : undefined
                                                        },
                                                        '&.Mui-disabled': {
                                                            backgroundColor: deadlineExpired ? 'action.disabledBackground' : undefined
                                                        }
                                                    }}
                                                >
                                                    {deadlineExpired ? t('postAssignmentDialog.expired') : 
                                                     isAlreadyAssigned ? t('postAssignmentDialog.alreadyAssigned') :
                                                     (isAssigned ? t('postAssignmentDialog.assigned') : t('postAssignmentDialog.assign'))}
                                                </Button>
                                            </ListItem>
                                        );
                                    })}
                                    {/* Loading indicator for infinite scroll */}
                                    {isLoadingMore && (
                                        <ListItem sx={{ justifyContent: 'center', py: 2 }}>
                                            <CircularProgress size={24} />
                                        </ListItem>
                                    )}
                                </>
                            ) : (
                                <ListItem>
                                    <ListItemText
                                        primary={t('postAssignmentDialog.noPositionsAvailable')}
                                        secondary={t('postAssignmentDialog.noPositionsDescription')}
                                    />
                                </ListItem>
                            )}
                        </List>
                    </>
                )}
            </DialogContent>

            {!showConfirmation && (
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        disabled={isSaving}
                    >
                        {t('postAssignmentDialog.cancel')}
                    </Button>
                    <Tooltip
                        title={!hasChanges() ? t('postAssignmentDialog.makeChangesBeforeSaving') : ""}
                        placement="top"
                    >
                        <span>
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={isSaving || !hasChanges()}
                                startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
                                sx={{ minWidth: 100 }}
                            >
                                {isSaving ? t('postAssignmentDialog.assigning') : t('postAssignmentDialog.assignButton')}
                            </Button>
                        </span>
                    </Tooltip>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default PostAssignmentDialog;